import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, filter, first, map, mergeMap, Observable, Subject, takeUntil, tap } from 'rxjs';
import { LgvClientService, LgvTSHelper, LgvUserService, NotificationService } from '../../core/core.module';
import { LgvCompanyService } from '../../core/services/lgv-company.service';
import { LgvLoanApplicationService } from '../../core/services/lgv-loan-application.service';
import { LgvMockDataService } from '../../core/services/lgv-mock-data.service';
import { ContainerHeightService } from '../../core/services/container-height.service';
import { GreaterThanZero } from '../../core/utility/lgv-form-validator';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { LgvModel } from '../../data-models/longevity.model';
import { StdObj } from '../../data-models/standard-objects';
import { CreateAccountModalComponent } from '../create-account-modal/create-account-modal.component';
import { InfoPopup } from '../info-popup/info-popup.component';
import { environment as env } from './../../../environments/environment';
import { LgvPricingModel } from '../../features/pricing/models/pricing.model';
import { LgvPricingService } from '../../features/pricing/services/pricing.service';
import { PricingSearchList } from '../../core/consts/pricing-scenario.const';
import { EmailPopup } from '../email-popup/email-popup.component';
import { LocationService } from '../../core/services/location.service';
import { ClientInitiatedLoanStepperComponent } from '../../features/steppers/components/stepper/client-initiated-loan-stepper/client-initiated-loan-stepper.component';

const DEFAULT_DURATION = 300;

@Component({
    selector: 'lgv-quick-pricer',
    templateUrl: './quick-pricer.component.html',
    styleUrls: ['./quick-pricer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,

    animations: [
        trigger('collapse', [
            state('false', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
            state('true', style({ height: '0', visibility: 'hidden' })),
            transition('false => true', animate(DEFAULT_DURATION + 'ms ease-in')),
            transition('true => false', animate(DEFAULT_DURATION + 'ms ease-out'))
        ])
    ]
})
export class QuickPricerComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() agentIdParam;
    @Input() mode = 'Public';
    @Input() primaryColor: string;
    @ViewChild('outerContainer') outerContainerRef: ElementRef;

    listsReady = false;
    firstSearch = true;
    onDestroy$ = new Subject();
    isLoadingAgentInfo$ = new BehaviorSubject(false);
    isLoadingResults$ = new BehaviorSubject(false);

    loanAppCompany: LgvModel.ApplicationCompany = null;

    _agentDTO: LgvModel.AgentDTO = null;
    agentDTO$ = new BehaviorSubject(this._agentDTO);
    _locationInfo: LgvModel.locationInfo = null;
    locationInfo$ = new BehaviorSubject(this._locationInfo);
    companyInfo$ = new BehaviorSubject(null);
    pricingProviderCode$ = new BehaviorSubject(null);
    showAdvancedOptions$ = new BehaviorSubject(false);
    isPurchase$ = new BehaviorSubject(false);
    isRefi$ = new BehaviorSubject(false);
    agentHasApplyUrl$ = new BehaviorSubject(false);
    locationDisplay$ = new BehaviorSubject('');
    todaysDate = new Date();

    //pageMode$ = new BehaviorSubject('SearchForm');
    searchForm: UntypedFormGroup;
    obSearchForm: UntypedFormGroup;

    _SearchResult: LgvPricingModel.SearchResult = null;
    SearchResult$ = new BehaviorSubject(this._SearchResult);

    productTypes: StdObj.ListItem[] = [];

    _productsByType: LgvPricingModel.Product[] = null;
    productsByType$ = new BehaviorSubject(this._productsByType);

    _downAmt: number = 0;
    downAmt$ = new BehaviorSubject(this._downAmt);

    rawLoanModelData: LgvLoanApp.ApplicationLoanInfo = null;
    savePricingSearchModel: LgvPricingModel.ProductSearch;

    get Products$(): Observable<LgvPricingModel.Product[]> { return this.SearchResult$.pipe(map(x => { return x === null ? null : x.products })) }
    get isClientMode(): boolean { return this.mode === 'Client'; }
    get isPublicMode(): boolean { return this.mode === 'Public'; }

    ComputeLTV$ = new BehaviorSubject(0);
    ComputeCLTV$ = new BehaviorSubject(0);

    //#region Lookups
    LoanPurposeList: StdObj.ListItem[];
    LoanTypesList: StdObj.ListItem[];
    LoanTermList: StdObj.ListItem[];
    AmortizationTypeList: StdObj.ListItem[];
    ArmFixedTermList: StdObj.ListItem[];
    AssetDocumentationList: StdObj.ListItem[];
    EmploymentDocumentationList: StdObj.ListItem[];
    IncomeDocumentationList: StdObj.ListItem[];
    CitizenshipList: StdObj.ListItem[];
    LienTypeList: StdObj.ListItem[];
    AmortizationTypesList: StdObj.ListItem[];
    PrepaymentPenaltyList: StdObj.ListItem[];
    OccupancyList: StdObj.ListItem[];
    PropertyTypeList: StdObj.ListItem[];
    NumberOfUnitsList: StdObj.ListItem[];
    TypeOfVeteranList: StdObj.ListItem[];
    AutomatedUnderwritingSystemList: StdObj.ListItem[];
    IncomeVerificationTypeList: StdObj.ListItem[];
    HousingEventTypeList: StdObj.ListItem[];
    HousingEventSeasoningList: StdObj.ListItem[];
    BankruptcyTypeList: StdObj.ListItem[];
    BankruptcyOutcomeList: StdObj.ListItem[];
    BankruptcySeasoningList: StdObj.ListItem[];
    monthsReservesList: StdObj.NumericListItem[];
    docTypeList: StdObj.ListItem[];    //#endregion Lookups
    clientInfo = {}
    clientAddress = {}

    //#region Form Properties
    get borrowerInformationFG(): AbstractControl { return this.searchForm.get('borrowerInformation'); }
    get coBorrowerInformationFG(): AbstractControl { return this.searchForm.get('coBorrowerInformation'); }
    get loanInformationFG(): AbstractControl { return this.searchForm.get('loanInformation'); }
    get propertyInformationFG(): AbstractControl { return this.searchForm.get('propertyInformation'); }

    get salesPriceField(): AbstractControl { return this.propertyInformationFG.get('salesPrice'); }
    get baseLoanAmountField(): AbstractControl { return this.loanInformationFG.get('baseLoanAmount'); }
    get obLoanTypeField(): AbstractControl { return this.loanInformationFG.get('loanType'); }
    get lpLoanTypesField(): AbstractControl { return this.loanInformationFG.get('loanTypes'); }
    get appraisedValueField(): AbstractControl { return this.propertyInformationFG.get('appraisedValue'); }
    get selectedOBLoanTypeValue(): string { return this.obLoanTypeField.value; }
    get debtToIncomeRatioCtrl(): AbstractControl { return this.borrowerInformationFG.get('debtToIncomeRatio'); }
    get monthlyDebtCtrl(): AbstractControl { return this.borrowerInformationFG.get('monthlyDebt'); }
    get monthlyIncomeCtrl(): AbstractControl { return this.loanInformationFG.get('totalMonthlyQualifyingIncome'); }
    get typeOfVeteranCtrl(): AbstractControl { return this.borrowerInformationFG.get('typeOfVeteran'); }
    get vaFirstTimeUseCtrl(): AbstractControl { return this.borrowerInformationFG.get('vaFirstTimeUse'); }
    get exemptFromVAFundingFeeCtrl(): AbstractControl { return this.loanInformationFG.get('exemptFromVAFundingFee'); }
    get firstLienAmountCtrl(): AbstractControl { return this.loanInformationFG.get('firstLienAmount'); }
    get lienTypeCtrl(): AbstractControl { return this.loanInformationFG.get('lienType'); }
    get loanPurposeCtrl(): AbstractControl { return this.loanInformationFG.get('loanPurpose'); }
    get downPctCtrl(): AbstractControl { return this.loanInformationFG.get('downPct'); }
    get zipCodeFG(): AbstractControl { return this.propertyInformationFG.get('zipCode'); }
    get stateFG(): AbstractControl { return this.propertyInformationFG.get('state'); }
    get countyFG(): AbstractControl { return this.propertyInformationFG.get('county'); }

    get isVALoan(): boolean {
        return this.selectedOBLoanTypeValue === 'VA'
    }

    locationState$ = new BehaviorSubject(null);
    //#endregion Form Properties

    constructor(
        private fb: UntypedFormBuilder,
        private http: HttpClient,
        public lgvPricingSvc: LgvPricingService,
        public matDialog: MatDialog,
        private lgvCompanySvc: LgvCompanyService,
        private notificationSvc: NotificationService,
        private lgvUserSvc: LgvUserService,
        private lgvLoanAppSvc: LgvLoanApplicationService,
        private containerHeightService: ContainerHeightService,
        public InfoPopupDialog: MatDialog,
        public lgvMockDataSvc: LgvMockDataService,
        private lgvClientSvc: LgvClientService,
        private locationService: LocationService,
    ) { }

    ngOnInit(): void {
        this.initAgent();
        const { SnapShot$, ownedProperties$ } = this.lgvClientSvc;
        SnapShot$.pipe(
            filter(ss => !!ss),
            takeUntil(this.onDestroy$),
            tap(ss => {
                this.clientInfo = ss.clientInfo;
            })
        ).subscribe();

        ownedProperties$.pipe(
            takeUntil(this.onDestroy$),
            first(properties => properties !== null),
            map(properties => properties.filter(p => p.isPrimaryProperty)),
            map(filtered => (filtered.length > 0 ? filtered[0] : null)),
            tap(primary => {
                this.clientAddress = primary;
            })
        ).subscribe();
    }

    ngAfterViewInit() {
        this.containerHeightService.startIframeHeightReporting(
            'qp', 
            this.outerContainerRef
        );
    }

    ngOnDestroy() {
        this.containerHeightService.stopHeightReporting('qp');
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    initAgent() {
        if (!(!!this.agentIdParam)) {
            this.notificationSvc.error('Agent ID was not provided');
            return;
        }

        this.isLoadingAgentInfo$.next(true);
        this.lgvUserSvc.InitAgentInfoFromPublicZone(this.agentIdParam);

        this.lgvUserSvc.AgentDTO$.pipe(first(), takeUntil(this.onDestroy$),
            mergeMap((userResponse) => {
                this.agentDTO$.next(userResponse);
                let hasApplyNowUrl = userResponse?.agentInfo?.applicationUrl && userResponse?.agentInfo?.applicationUrl.length > 0;
                this.agentHasApplyUrl$.next(hasApplyNowUrl);
                return this.lgvCompanySvc.getPublicCompanyInfoById(userResponse.company.id).pipe(first(), takeUntil(this.onDestroy$));
            })
        )
            .subscribe((companyResponse) => {
                this.isLoadingAgentInfo$.next(false);
                this.companyInfo$.next(companyResponse);

                this.lgvCompanySvc.setActiveCompany(companyResponse.companyCode);

                this.loanAppCompany = companyResponse.applicationCompanies.find(ac => ac.type === 'LoanApplications');
                let pricingAppCompany = companyResponse.applicationCompanies.find(ac => ac.type === 'Pricing');

                if (!!pricingAppCompany) {
                    if (pricingAppCompany.name === 'LenderPrice') {
                        this.pricingProviderCode$.next('LP');
                    }
                    if (pricingAppCompany.name === 'OptimalBlue') {
                        this.pricingProviderCode$.next('OB');
                    }

                    this.initLists();
                    this.initSearchForm();
                }

                if (!(!!this.pricingProviderCode$.value)) {
                    this.notificationSvc.error('Unable to obtain pricing configuration.')
                }

            },
                err => {
                    this.isLoadingAgentInfo$.next(false);
                    console.log('Error fetching agent info.');
                    this.notificationSvc.error('There was an error fetching agent information.');
                }
            );
    }

    initLists() {

        this.AmortizationTypeList = PricingSearchList.amortizationTypeList;
        this.LoanPurposeList = PricingSearchList.loanPurposeList;
        this.CitizenshipList = PricingSearchList.citizenshipList;
        this.LoanTypesList = PricingSearchList.loanTypeList;
        this.LoanTermList = PricingSearchList.loanTermList;
        this.OccupancyList = PricingSearchList.occupancyList;
        this.TypeOfVeteranList = PricingSearchList.typeOfVeteran;
        this.PropertyTypeList = PricingSearchList.propertyTypeList;

        this.ArmFixedTermList = PricingSearchList.armFixedTermList;
        this.LienTypeList = PricingSearchList.lienTypeList;
        this.PrepaymentPenaltyList = PricingSearchList.prepaymentPenaltyList;
        this.NumberOfUnitsList = PricingSearchList.numberOfUnitsList;
        this.HousingEventTypeList = PricingSearchList.housingEventTypeList;
        this.HousingEventSeasoningList = PricingSearchList.housingEventSeasoningList;
        this.BankruptcyTypeList = PricingSearchList.bankruptcyTypeList;
        this.BankruptcyOutcomeList = PricingSearchList.bankruptcyOutcomeList;
        this.BankruptcySeasoningList = PricingSearchList.bankruptcySeasoningList;
        this.monthsReservesList = PricingSearchList.monthsReserves;
        this.docTypeList = PricingSearchList.doctTypeList;
        this.listsReady = true;
    }

    initSearchForm() {
        this.searchForm = this.fb.group({

            borrowerInformation: this.fb.group({
                //citizenship: [null, [Validators.required]],
                selfEmployed: [false],
                firstTimeHomeBuyer: [false],
                waiveEscrows: [false],
                fico: [null, [Validators.required, GreaterThanZero()]],
                debtToIncomeRatio: [null, [Validators.required, GreaterThanZero()]],
                monthlyDebt: [null, []],
                monthsReserves: [null, []],
                docType: [null],
                typeOfVeteran: [null],
                vaFirstTimeUse: [false]
            }),

            coBorrowerInformation: this.fb.group({
                intentToOccupy: [true]
            }),

            loanInformation: this.fb.group({
                lienType: [null, [Validators.required]],
                baseLoanAmount: [400000, [Validators.required, GreaterThanZero()]],
                loanPurpose: [null, [Validators.required]],
                loanType: [null, [Validators.required]],
                loanTerms: [null, [Validators.required]],
                armFixedTerms: [null, [Validators.required]],
                amortizationTypes: [null, [Validators.required]],
                firstLienAmount: [null, []],
                interestOnly: [false],
                cashOutAmount: [0],
                downPct: [20],
                exemptFromVAFundingFee: [false],
                totalMonthlyQualifyingIncome: [0, []],
            }),

            propertyInformation: this.fb.group({
                appraisedValue: [500000, [Validators.required, GreaterThanZero()]],
                occupancy: [null, [Validators.required]],
                propertyType: [null, [Validators.required]],
                salesPrice: [null, [Validators.required, GreaterThanZero()]],
                state: [null, [Validators.required]],
                zipCode: [null, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
                county: [null, [Validators.required]],
                numberOfUnits: [null, [Validators.required]],
                numberOfStories: [null, [Validators.required]],
            })

        });


        // Keep sales pric in sync w/ appraisedValue (like Nexus Pricing)
        this.appraisedValueField.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            const salesPrice = this.salesPriceField;
            salesPrice.setValue(x);

            this.updateLTV(this.baseLoanAmountField.value, x);
            this.updateCLTV(this.baseLoanAmountField.value, this.firstLienAmountCtrl.value, x);
            this.setDownAndLoanAmt();

        });

        this.obLoanTypeField.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            if (this.isVALoan) {
                this.typeOfVeteranCtrl.setValidators([Validators.required]);
                this.exemptFromVAFundingFeeCtrl.setValidators([Validators.required]);
                this.vaFirstTimeUseCtrl.setValidators([Validators.required]);
                this.exemptFromVAFundingFeeCtrl.setValue(false);
                this.vaFirstTimeUseCtrl.setValue(false);
            }
            else {
                this.typeOfVeteranCtrl.setValidators([]);
                this.exemptFromVAFundingFeeCtrl.setValidators([]);
                this.vaFirstTimeUseCtrl.setValidators([]);
                this.typeOfVeteranCtrl.setValue(null);
                this.exemptFromVAFundingFeeCtrl.setValue(null);
                this.vaFirstTimeUseCtrl.setValue(null);
            }
            // Force Validation
            this.searchForm.markAllAsTouched();
        });

        this.loanPurposeCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            let newPurpose = x as string;
            this.isRefi$.next(newPurpose.toUpperCase().indexOf("REFI") !== -1);
            this.isPurchase$.next(x === 'Purchase');

            if (this.isPurchase$.value) {
                this.baseLoanAmountField.disable();
            }
            else {
                this.baseLoanAmountField.enable();
            }

            this.setDownAndLoanAmt();
        });

        this.baseLoanAmountField.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            this.updateLTV(x, this.appraisedValueField.value);
            this.updateCLTV(x, this.firstLienAmountCtrl.value, this.appraisedValueField.value);
        });

        this.downPctCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            this.setDownAndLoanAmt();
        });

        this.zipCodeFG.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            let newZip = x as string;

            let haveSavedLocationInfo = !!this.locationInfo$.value
                && this.locationInfo$.value?.postalCode
                && this.locationInfo$.value?.postalCode.length >= 5
                && this.locationInfo$.value?.county
                && this.locationInfo$.value?.county.length > 0;

            if (newZip && newZip.length >= 5 && (!haveSavedLocationInfo || this.locationInfo$.value.postalCode !== newZip)) {
                this.lookUpLocationInfoFromZip(newZip);
            }
        });

        this.monthlyDebtCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            this.checkDTIValidation('monthlyDebt');
        });

        this.monthlyIncomeCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            this.checkDTIValidation('monthlyIncome');
        });

        // Check for VA loan type and set the validation accordingly (required only if LoanType = 'VA')
        this.loanInformationFG.get('loanType').valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            if (this.isVALoan) {
                this.typeOfVeteranCtrl.setValidators([]);
            }
            else {
                this.typeOfVeteranCtrl.setValidators([]);
                this.typeOfVeteranCtrl.setValue(null);
            }
            // Force Validation
            this.searchForm.markAllAsTouched();

        });

        this.setDefaultValues();
        this.geoLocationLookup();
    }

    checkDTIValidation(ctrlChanged: string) {
        /*
         * Rule:  If debt or income present, both are required and we need to calc DTI
         * otherwise, they are not required, and set DTI to 30
         */

        let monthlyDebt = this.monthlyDebtCtrl.value as number;
        let monthlyIncome = this.monthlyIncomeCtrl.value as number;

        let haveDebt = !!monthlyDebt && monthlyDebt > 0;
        let haveIncome = !!monthlyIncome && monthlyIncome > 0;

        if (haveDebt || haveIncome) {

            if (!this.monthlyDebtCtrl.hasValidator(Validators.required)) {
                this.monthlyDebtCtrl.setValidators([Validators.required]);
                this.monthlyDebtCtrl.updateValueAndValidity();
            }
            if (!this.monthlyIncomeCtrl.hasValidator(Validators.required)) {
                this.monthlyIncomeCtrl.setValidators([Validators.required, this.DTIValidator()]);
                this.monthlyIncomeCtrl.updateValueAndValidity();
            }

            if (haveDebt && haveIncome && ctrlChanged === 'monthlyDebt') {
                this.monthlyIncomeCtrl.updateValueAndValidity();
            }

            this.debtToIncomeRatioCtrl.setValue((haveDebt && haveIncome) ? (monthlyDebt / monthlyIncome) * 100 : null);
            this.debtToIncomeRatioCtrl.updateValueAndValidity();
        }
        else {
            if (this.monthlyDebtCtrl.value) {
                this.monthlyDebtCtrl.setValue(null);
            }

            if (this.monthlyIncomeCtrl.value) {
                this.monthlyIncomeCtrl.setValue(null);
            }

            if (this.monthlyDebtCtrl.hasValidator(Validators.required)) {
                this.monthlyDebtCtrl.clearValidators();
                this.monthlyDebtCtrl.updateValueAndValidity();
            }

            if (this.monthlyIncomeCtrl.hasValidator(Validators.required)) {
                this.monthlyIncomeCtrl.clearValidators();
                this.monthlyIncomeCtrl.updateValueAndValidity();
            }

            this.debtToIncomeRatioCtrl.setValue(30);
        }

    }

    geoLocationLookup() {
        const bldUrl = `${env.LgvApiBaseUrl}/public/geolocation`;

        return this.http.get<LgvModel.locationInfo>(bldUrl).pipe(first(), takeUntil(this.onDestroy$)).subscribe(locationInfo => {

            if (!!locationInfo && !!locationInfo.postalCode && locationInfo.postalCode.length > 0) {
                this.locationState$.next(locationInfo);
                this.zipCodeFG.setValue(locationInfo.postalCode);
                this.countyFG.setValue(locationInfo.county);
                this.stateFG.setValue(locationInfo.stateCode);
            }
            else {
                // if we can't get geocode location, try getting the agent's zipcode
                this.setZipcodeFromAgent();
            }
        },
            (err) => {
                //this.notificationSvc.error(`Unable to get location information for ${zipCode}`);
                console.log('Error looking up geocode info');
                console.log(err)
                this.setZipcodeFromAgent();
            }
        );
    }

    setZipcodeFromAgent() {
        if (!!this.agentDTO$.value?.agentInfo && !!this.agentDTO$.value?.agentInfo?.zipCode && this.agentDTO$.value?.agentInfo?.zipCode.length > 0) {
            this.zipCodeFG.setValue(this.agentDTO$.value.agentInfo.zipCode);

        }
    }

    lookUpLocationInfoFromZip(zipCode: string) {
        // Use the LocationService to get location info
        const userId = this.agentDTO$.value?.agentInfo?.id;

        this.locationService.lookupLocationByZip(zipCode, userId)
            .subscribe(
                locationInfo => {
                    if (locationInfo) {
                        this.countyFG.setValue(locationInfo.county);
                        this.stateFG.setValue(locationInfo.stateCode);
                        this.locationDisplay$.next(`${locationInfo.county}, ${locationInfo.stateCode}`);
                        this.locationState$.next({
                            postalCode: zipCode,
                            county: locationInfo.county,
                            stateCode: locationInfo.stateCode
                        });
                        this.searchForm.updateValueAndValidity();

                        // do an automatic initial search
                        if (this.searchForm.valid && this.firstSearch) {
                            this.firstSearch = false;
                            this.onSearchProducts(false);
                        }
                    } else {
                        // Handle no locations returned (user not licensed in this state)
                        this.notificationSvc.error(`No licensed locations found for zipcode ${zipCode}. Please verify you are licensed in this state.`);
                        // Clear both zip and state fields
                        this.zipCodeFG.setValue(null);
                        this.stateFG.setValue(null);
                        this.countyFG.setValue(null);
                    }
                },
                (err) => {
                    this.notificationSvc.error(`Unable to get location information for ${zipCode}`);
                    console.log('Error looking up zipcode info');
                    console.log(err);
                    // Clear both zip and state fields
                    this.zipCodeFG.setValue(null);
                    this.stateFG.setValue(null);
                    this.countyFG.setValue(null);
                }
            );

        // const bldUrl = `${env.LgvApiBaseUrl}/public/locationDetails?$filter=postalCode eq '${zipCode}'${this.agentIdParam ? '&userId=' + this.agentIdParam : ''}`;

        // return this.http.get<any>(bldUrl).pipe(first(), takeUntil(this.onDestroy$)).subscribe(x => {
        //     // Check if response has any values
        //     if (x?.value && x.value.length > 0) {
        //         const locationInfo = x.value[0] as LgvModel.locationInfo;
        //         this.countyFG.setValue(locationInfo.county);
        //         this.stateFG.setValue(locationInfo.stateCode);
        //         this.locationDisplay$.next(`${locationInfo.county}, ${locationInfo.stateCode}`);
        //         this.locationState$.next({
        //             postalCode: zipCode,
        //             county: locationInfo.county,
        //             stateCode: locationInfo.stateCode
        //         });
        //         this.searchForm.updateValueAndValidity();

        //         // do an automatic initial search
        //         if (this.searchForm.valid && this.firstSearch) {
        //             this.firstSearch = false;
        //             this.onSearchProducts(false);
        //         }
        //     } else {
        //         this.notificationSvc.warn(`No location information found for ${zipCode} or agent is not licensed in this location.`);
        //         console.log('Location details returned empty array or no values');
        //     }
        // },
        //     (err) => {
        //         this.notificationSvc.error(`Unable to get location information for ${zipCode}`);
        //         console.log('Error looking up zipcode info');
        //         console.log(err);
        //     }
        // );

    }

    updateLTV(loanAmt: any, valueAmt: any) {
        const val1: number = loanAmt || 0;
        const val2: number = valueAmt || 0;

        if (val1 && val1 > 0 && val2 && val2 > 0) {
            this.ComputeLTV$.next(+(((val1 / val2) * 100).toFixed(2)));
        }
        else {
            this.ComputeLTV$.next(0);
        }

    }

    updateCLTV(loanAmt: any, firstLienAmt, valueAmt: any) {

        const val1: number = (loanAmt || 0) + (firstLienAmt || 0);
        const val2: number = valueAmt || 0;

        if (val1 && val1 > 0 && val2 && val2 > 0) {
            this.ComputeCLTV$.next(+(((val1 / val2) * 100).toFixed(2)));
        }
        else {
            this.ComputeCLTV$.next(0);
        }
    }

    setDefaultValues() {

        this.productsByType$.next([]);

        const model = this.lgvPricingSvc.defaultOBProductSearch();
        model.loanInformation.loanType = 'Conventional';
        model.loanInformation.totalMonthlyQualifyingIncome = 0;
        model.loanInformation.loanPurpose = 'Purchase';
        model.propertyInformation.appraisedValue = 500000;
        model.loanInformation.loanTerms = ["ThirtyYear", "TwentyYear", "FifteenYear"];

        model.loanInformation.lienType = 'First';
        // add any custom defaulting here...
        this.searchForm.patchValue(model);
    }

    onSearchProducts(userClick: boolean) {
        this.rawLoanModelData = {
            loanAmount: this.baseLoanAmountField.value,
            purpose: { name: this.loanPurposeCtrl.value },
            appraisalValue: this.appraisedValueField.value,
        }

        this.productTypes = [];
        this.productsByType$.next([]);

        const model = this.lgvPricingSvc.buildSearchModel(this.searchForm.getRawValue());

        this.SearchResult$.next(null);
        this.isLoadingResults$.next(true);
        this.lgvPricingSvc.productSearchPublicNoClient(this.agentDTO$.value.agentInfo.id, model).pipe(first(), takeUntil(this.onDestroy$)).subscribe(x => {
            this.SearchResult$.next(x);
            this.isLoadingResults$.next(false);
            this.buildProductTypeList(x);
        },
            (err) => {
                this.isLoadingResults$.next(false);
                this.notificationSvc.error("There was an error fetching pricing: " + err)
            }
        );

        const searchDescr = `$${this.downAmt$.value} down on $${model.propertyInformation.appraisedValue} - fico: ${model.borrowerInformation.fico}`;
        this.lgvClientSvc.PostRateSearch(searchDescr).pipe(first()).subscribe();
    }

    buildProductTypeList(results: LgvPricingModel.SearchResult) {
        this.productTypes = [];
        const allProductTypes = results.products.map(x => x.keyLabel);
        const uniqueProductTypes = allProductTypes.filter(LgvTSHelper.onlyUnique);
        const sortedUniqueProductTypes = uniqueProductTypes.sort();

        let buildProducts = [];
        let allProducts = this.SearchResult$.value.products;


        for (let x of sortedUniqueProductTypes) {
            this.productTypes.push({ ID: x, Display: x, IsSelected: false })

            let productsForType = allProducts.filter(f => f.keyLabel === x);
            let sortedProductsForType = productsForType.sort((a, b) => this.lgvPricingSvc.loanProductCompareOrder(a, b));
            if (sortedProductsForType && sortedProductsForType.length > 0) {
                buildProducts.push(sortedProductsForType[0]);
            }
        }

        this.productsByType$.next(buildProducts);
    }

    setDownAndLoanAmt() {

        if (!this.isPurchase$.value) {
            return;
        }

        let appraisal = this.appraisedValueField.value;
        let downPct = this.downPctCtrl.value;
        if (appraisal && appraisal > 0 && downPct !== null) {

            this.downAmt$.next(appraisal * downPct * .01);
            //return of(appraisal * downPct * .01)
        }
        else {
            this.downAmt$.next(null);
        }

        this.baseLoanAmountField.setValue(appraisal - this.downAmt$.value);
    }

    onToggleAdvancedOptions() {
        let isExpanded = this.showAdvancedOptions$.value;
        this.showAdvancedOptions$.next(!isExpanded);
    }

    productNameDisplay(name: string) {
        return name.replace(' Purchase', '').replace(' Refinance', '');
    }

    onReset() {
        this.setDefaultValues();
        this.zipCodeFG.setValue(this.locationState$.value?.postalCode);
        this.countyFG.setValue(this.locationState$.value?.county);
        this.stateFG.setValue(this.locationState$.value?.stateCode);
    }

    openInfoPopup(key: string): void {
        const dialogRef = this.InfoPopupDialog.open(InfoPopup, {
            width: '500px',
            data: { mode: key },
            hasBackdrop: true,
            backdropClass: 'transparent-backdrop',
            panelClass: 'info-popup-dialog'
        });
    }

    buildLoanInfoFromProductResult(product: LgvPricingModel.Product): LgvLoanApp.ApplicationLoanInfo {
        return this.lgvLoanAppSvc.buildLoanInfoModelFromPricingSearch(this.savePricingSearchModel, product);
    }

    //buildLenderLoanInfoFromProductResult(product: LgvPricingScenarioModel.Product): New_LgvLoanApp. {
    //    return ApplyNowBtnComponent.convertProductToLenderLoanInfoModel(product);
    //}

    openCreateAccountModal() {
        this.matDialog.open(CreateAccountModalComponent, {
            data: {
                agentId: this.agentIdParam
            }
        });
    }

    onSendMessage(product: LgvPricingModel.Product): void {
        var message = `<b>Product: ${product.keyLabel} - ${product.productName}\nType: ${product.loanType}\nTerm: ${product.loanTerm}\nPayment: ${product.totalPayment}\nRate: ${product.rate}%\nAPR: ${product.apr}%\nClosing Cost: ${product.closingCost}\nTotal Payment: ${product.totalPayment}\nRebate: ${product.rebate}\nDiscount: ${product.discount}`;

        const dialogRef = this.matDialog.open(EmailPopup, {
            width: '500px',
            data: { type: 'Rate Quote', message: message }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.lgvClientSvc.PostPageClick('Rate Quote Email Sent').pipe(first()).subscribe();
                this.notificationSvc.error('Rate Quote Email Sent');
            }
        });
    }

    DTIValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            let debt = +this.monthlyDebtCtrl.value;
            let income = +this.monthlyIncomeCtrl.value;

            let hasComparisonError = !!debt && !!income && debt >= income;
            // console.log(`DTI Comparison: ${debt} | ${income} | ${hasComparisonError} `);
            return hasComparisonError ? { 'comparison': true } : null;
        }
    }

    openStepper() {
        const data = {
            agentId: this.agentIdParam,
            clientInfo: this.isClientMode ? this.clientInfo : {},
            clientAddress: this.isClientMode ? this.clientAddress : {},
            fromQuickPricer: {
                loanPurpose: this.loanInformationFG.value.loanPurpose
            }
        }
        const dialogRef = this.matDialog.open(ClientInitiatedLoanStepperComponent, {
            width: '400px',
            panelClass: 'dialog-container',
            disableClose: true,
            data
        });
    }
}


